import * as cartApi from '../../helpers/cartApi';
import initCarousel from '../../helpers/carousel';

const TOTAL_RECOMMENDATIONS = 12;

const selectors = {
  crossSell: '[data-cart-cross-sell]',
  crossSellItem: '[data-cross-sell-item]',
  crossSellSlides: '[data-cross-sell-slides]',
  form: '[data-cross-sell-form]',
  crossSellSelect: '[data-cross-sell-select]',
};

/**
 * The wrapper class for Cross Sells
 *
 * @export
 * @class CartCrossSell
 */
export default class CartCrossSell {
  constructor(el, cartItems, updateCart) {
    this.el = el.querySelector(selectors.crossSell);
    this.updateCart = updateCart;
    this.cartItems = cartItems;

    if (!this.el) {
      return;
    }

    this._init();
  }

  _init = () => {
    this._iterateCartItems();
  };

  _attachListeners = () => {
    const forms = this.el.querySelectorAll(selectors.form);
    forms.forEach(form => {
      form.removeEventListener('submit', this._onSubmit);
      form.addEventListener('submit', this._onSubmit);
    });

    const selects = this.el.querySelectorAll(selectors.crossSellSelect);
    selects.forEach(select => {
      select.removeEventListener('change', this._onChange);
      select.addEventListener('change', this._onChange);
    });
  };

  _onChange = event => {
    const form = event.target.closest(selectors.form);
    const slide = form.closest(selectors.crossSellItem);
    const selectedOptions = form.querySelectorAll('option:checked');
    const selectedOptionValues = Array.from(selectedOptions).map(
      option => option.dataset.optionValueId,
    );

    const params =
      selectedOptionValues.length > 0
        ? `&option_values=${selectedOptionValues.join(',')}`
        : '';

    this._fetchProductHtml(form.dataset.productHandle, params, slide);
  };

  _onSubmit = event => {
    event.preventDefault();

    const data = cartApi.formatFormData(new FormData(event.currentTarget));

    cartApi.add({ items: [data] }).then(res => {
      if (res.sections) {
        this.updateCart({ sections: res.sections });
      }
    });
  };

  _iterateCartItems = async () => {
    const productIds = Array.from(this.cartItems).map(
      item => item.dataset.productId,
    );
    const limitPerItem = Math.floor(TOTAL_RECOMMENDATIONS / productIds.length);

    if (limitPerItem === 0) {
      this.el.hidden = true;
      return;
    }

    const products = await this._fetchComplementaryProducts(
      productIds,
      limitPerItem,
    );

    if (products.length > 0) {
      await this._renderSlides(products);
      this._initCarousel();
    } else {
      this.el.hidden = true;
    }
  };

  _fetchComplementaryProducts = async (productIds, limitPerItem) => {
    const responses = await Promise.all(
      productIds.map(productId =>
        fetch(
          `${Shopify.routes.root}recommendations/products.json?section_id=main-cart-cross-sell&product_id=${productId}&limit=${limitPerItem}&intent=related`,
        ).then(response => response.json()),
      ),
    );

    const cartProductIds = new Set(productIds.map(String));
    const seen = new Set();
    const products = [];

    for (const response of responses) {
      for (const product of response.products ?? []) {
        const id = String(product.id);

        if (seen.has(id) || cartProductIds.has(id)) {
          continue;
        }

        seen.add(id);
        products.push(product);

        if (products.length >= TOTAL_RECOMMENDATIONS) {
          return products;
        }
      }
    }

    return products;
  };

  _renderSlides = async products => {
    const slidesEl = this.el.querySelector(selectors.crossSellSlides);

    if (!slidesEl) {
      return;
    }

    slidesEl.innerHTML = '';

    const slideHtmlList = await Promise.all(
      products.map(product =>
        this._fetchProductHtml(product.handle, '', null, true),
      ),
    );

    slideHtmlList.forEach(html => {
      if (html) {
        slidesEl.insertAdjacentHTML('beforeend', html);
      }
    });

    this._attachListeners();
  };

  _fetchProductHtml = async (
    handle,
    params = '',
    targetSlide = null,
    returnHtml = false,
  ) => {
    const response = await fetch(
      `${Shopify.routes.root}products/${handle}?section_id=main-cart-cross-sell${params}`,
    );
    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const crossSellItem = html.querySelector(selectors.crossSellItem);

    if (!crossSellItem) {
      return returnHtml ? null : undefined;
    }

    if (returnHtml) {
      return crossSellItem.outerHTML;
    }

    if (targetSlide) {
      targetSlide.outerHTML = crossSellItem.outerHTML;
      this._attachListeners();
      return;
    }

    const slidesEl = this.el.querySelector(selectors.crossSellSlides);

    if (slidesEl) {
      slidesEl.innerHTML = crossSellItem.outerHTML;
    } else {
      this.el.innerHTML = crossSellItem.innerHTML;
    }

    this._attachListeners();
  };

  _initCarousel = () => {
    const slidesEl = this.el.querySelector(selectors.crossSellSlides);

    if (!slidesEl?.children.length) {
      return;
    }

    initCarousel(
      this.el,
      {
        perPage: 1,
        gap: '0.5rem',
        pagination: false,
        arrows: true,
        padding: {
          right: '15%',
        },
      },
      true,
    );
  };
}
